import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Form, Radio, Button, Modal, SelectOption } from 'tea-component';
import DownLoadBubble from '@/pages/intelligence/components/DownLoad/DownLoadBubble';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { DOWNLOAD_TITLE_TIPS } from '@intelligence/components/ExportTable/instance/const';

import type { IBaseProps } from '@/pages/intelligence/types/common';

import "@intelligence/components/ExportTable/index.scss";

const IMAGE_FILE_FORMATS = [
  {
    text: 'PNG',
    value: 'png',
  },
  {
    text: 'PDF',
    value: 'pdf',
  },
];

export interface ISimpleDownloadFormModel {
  fileFormat: string;
}

interface ISimpleDownloadSelectProps extends IBaseProps {
  disabled: boolean;
  loading: boolean;
  fileFormats?: SelectOption[];
  downloadModalSize?: number | string;
  downloadModalClassName?: string;
  downloadModalStyle?: React.CSSProperties;
  downloadTitle?: string | React.ReactElement;
  downloadTitleTips?: string;
  bubbleContent?: string;
  hasBubbleCloseIcon?: boolean;
  onBubbleClose?: () => void;
  onSelect: (value: ISimpleDownloadFormModel) => void;
}

const SimpleDownloadSelect: React.FC<ISimpleDownloadSelectProps> = (props) => {
  const { formatMessage } = useIntl();

  const {
    style,
    className,
    disabled,
    loading,
    fileFormats = IMAGE_FILE_FORMATS,
    downloadModalSize = 'm',
    downloadModalClassName,
    downloadModalStyle,
    downloadTitle = formatMessage({ id: 'dbi_download_settings' }),
    downloadTitleTips = DOWNLOAD_TITLE_TIPS,
    bubbleContent,
    hasBubbleCloseIcon = false,
    onBubbleClose,
    onSelect,
  } = props;

  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [formModel, setFormModel] = useState<ISimpleDownloadFormModel>({
    fileFormat: '',
  });

  const isFormValid = useMemo<boolean>(() => {
    return formModel?.fileFormat !== '';
  }, [formModel]);

  const handleDownloadPre = () => {
    if (loading) return;
    setFormModel({
      ...formModel,
      fileFormat: fileFormats?.[0]?.value || '',
    });
    setModalVisible(true);
  };

  const handleDownload = () => {
    if (loading) return;
    if (!isFormValid) return;
    setModalVisible(false);
    onSelect(formModel);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  return (
    <>
      <DownLoadBubble
        style={{ width: 400 }}
        hasCloseIcon={hasBubbleCloseIcon}
        visible={bubbleVisible}
        content={bubbleContent}
        onClose={() => {
          setBubbleVisible(false);
          onBubbleClose?.();
        }}
      >
        <Button
          icon={loading ? 'loading' : 'download'}
          style={{ ...style, cursor: loading ? 'default' : 'pointer' }}
          className={classnames('ns-btn--icon trend-justify-btn', className)}
          // title={formatMessage({ id: 'dbi_download_data' })}
          disabled={disabled}
          loading={loading}
          onClick={handleDownloadPre}
          onMouseEnter={() => setBubbleVisible(true)}
          onMouseLeave={() => setBubbleVisible(false)}
        />
      </DownLoadBubble>
      <Modal
        className={classnames('dbi-download-type', downloadModalClassName)}
        style={downloadModalStyle}
        size={downloadModalSize}
        visible={modalVisible}
        onClose={handleCloseModal}
        caption={
          <div className="download-head">
            <span>{downloadTitle}</span>
            {downloadTitleTips && (
              <TipContext className="tip-text-hd" tipText={formatMessage({ id: downloadTitleTips })} />  
            )}
          </div>
        }
      >
        <Modal.Body>
          <Form className="dbi-download-form teaui-form-alignment">
            <Form.Item label={formatMessage({ id: 'dbi_label_file_format' })}>
              <Radio.Group
                value={formModel?.fileFormat}
                onChange={(value) => {
                  setFormModel({ ...formModel, fileFormat: value });
                }}
              >
                {fileFormats?.map((item) => (
                  <Radio key={item?.value} name={item?.value}>
                    {item?.text}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            // loading={loading}
            loading={false}
            disabled={!isFormValid}
            onClick={handleDownload}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button type="weak" onClick={handleCloseModal}>
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SimpleDownloadSelect;
